import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UserProgress, ExerciseProgress, QuizResult } from '../types';

const STORAGE_KEY = 'learning-platform-progress';

const loadProgress = (): UserProgress => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const progress = JSON.parse(saved);

      // Add backward compatibility for existing data without quizResults
      if (progress.courseProgress) {
        Object.keys(progress.courseProgress).forEach(courseId => {
          if (!progress.courseProgress[courseId].quizResults) {
            progress.courseProgress[courseId].quizResults = {};
          }
        });
      }

      return progress;
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to load progress from localStorage:', error);
  }

  return {
    enrolledCourses: [],
    courseProgress: {},
  };
};

const saveProgress = (progress: UserProgress) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to save progress to localStorage:', error);
  }
};

const initialState: UserProgress = loadProgress();

const progressSlice = createSlice({
  name: 'progress',
  initialState,
  reducers: {
    enrollInCourse: (state, action: PayloadAction<string>) => {
      const courseId = action.payload;
      if (!state.enrolledCourses.includes(courseId)) {
        state.enrolledCourses.push(courseId);
        state.courseProgress[courseId] = {
          currentModule: '',
          completedModules: [],
          exerciseProgress: {},
          quizScores: {},
          quizResults: {},
          quizProgress: {},
          enrolledAt: new Date().toISOString(),
          lastAccessed: new Date().toISOString(),
        };
      }
      state.currentCourse = courseId;
      saveProgress(state);
    },

    setCurrentCourse: (state, action: PayloadAction<string>) => {
      state.currentCourse = action.payload;
      if (state.courseProgress[action.payload]) {
        state.courseProgress[action.payload].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    setCurrentModule: (state, action: PayloadAction<{ courseId: string; moduleId: string }>) => {
      const { courseId, moduleId } = action.payload;
      if (state.courseProgress[courseId]) {
        state.courseProgress[courseId].currentModule = moduleId;
        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    completeModule: (state, action: PayloadAction<{ courseId: string; moduleId: string }>) => {
      const { courseId, moduleId } = action.payload;
      if (
        state.courseProgress[courseId] &&
        !state.courseProgress[courseId].completedModules.includes(moduleId)
      ) {
        state.courseProgress[courseId].completedModules.push(moduleId);
        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    restartModule: (state, action: PayloadAction<{ courseId: string; moduleId: string }>) => {
      const { courseId, moduleId } = action.payload;
      if (state.courseProgress[courseId]) {
        // Remove module from completed modules
        state.courseProgress[courseId].completedModules = state.courseProgress[
          courseId
        ].completedModules.filter(id => id !== moduleId);

        // Clear all exercise progress for this module
        Object.keys(state.courseProgress[courseId].exerciseProgress).forEach(exerciseId => {
          if (exerciseId.startsWith(moduleId + '-')) {
            delete state.courseProgress[courseId].exerciseProgress[exerciseId];
          }
        });

        // Clear quiz results for this module
        Object.keys(state.courseProgress[courseId].quizResults).forEach(quizId => {
          if (quizId.startsWith(moduleId + '-')) {
            delete state.courseProgress[courseId].quizResults[quizId];
          }
        });

        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    updateExerciseProgress: (
      state,
      action: PayloadAction<{
        courseId: string;
        exerciseId: string;
        progress: Partial<ExerciseProgress>;
      }>
    ) => {
      const { courseId, exerciseId, progress } = action.payload;
      if (state.courseProgress[courseId]) {
        const current = state.courseProgress[courseId].exerciseProgress[exerciseId] || {
          completed: false,
          attempts: 0,
          hintsUsed: 0,
        };

        state.courseProgress[courseId].exerciseProgress[exerciseId] = {
          ...current,
          ...progress,
          lastAttempt: new Date().toISOString(),
        };
        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    updateQuizScore: (
      state,
      action: PayloadAction<{ courseId: string; quizId: string; score: number }>
    ) => {
      const { courseId, quizId, score } = action.payload;
      if (state.courseProgress[courseId]) {
        state.courseProgress[courseId].quizScores[quizId] = score;
        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    updateQuizResult: (
      state,
      action: PayloadAction<{ courseId: string; quizId: string; result: QuizResult }>
    ) => {
      const { courseId, quizId, result } = action.payload;
      if (state.courseProgress[courseId]) {
        // Ensure quizResults exists for backward compatibility
        if (!state.courseProgress[courseId].quizResults) {
          state.courseProgress[courseId].quizResults = {};
        }

        // Update both score and detailed result
        state.courseProgress[courseId].quizScores[quizId] = result.score;
        state.courseProgress[courseId].quizResults[quizId] = result;
        state.courseProgress[courseId].lastAccessed = new Date().toISOString();
      }
      saveProgress(state);
    },

    resetCourseProgress: (state, action: PayloadAction<string>) => {
      const courseId = action.payload;
      if (state.courseProgress[courseId]) {
        state.courseProgress[courseId] = {
          currentModule: '',
          completedModules: [],
          exerciseProgress: {},
          quizScores: {},
          quizResults: {},
          quizProgress: {},
          enrolledAt: state.courseProgress[courseId].enrolledAt,
          lastAccessed: new Date().toISOString(),
        };
      }
      saveProgress(state);
    },
  },
});

export const {
  enrollInCourse,
  setCurrentCourse,
  setCurrentModule,
  completeModule,
  restartModule,
  updateExerciseProgress,
  updateQuizScore,
  updateQuizResult,
  resetCourseProgress,
} = progressSlice.actions;

export default progressSlice.reducer;
